import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, MapPin, ChevronDown, Mail, Phone } from 'lucide-react';
import { Customer } from '../../lib/customers';
import AddressAutocomplete from '../AddressAutocomplete';

import { CustomerSource } from '../../lib/customerSources';

interface BookingCustomerInfoProps {
  customer: Customer;
  onEdit?: () => void;
  bookingAddress?: string;
  onEditBookingAddress?: (address: string) => void;
  mobile_booking?: boolean;
  canEdit?: boolean;
  customerNotes?: string;
  onEditCustomerNotes?: (notes: string) => void;
  bookingSource?: string | null;
  onSourceUpdate?: (source: string | null) => void;
  businessId?: string;
  bookingId?: string;
}

export default function BookingCustomerInfo({ 
  customer, 
  onEdit,
  bookingAddress,
  onEditBookingAddress,
  mobile_booking = false,
  canEdit = true,
  customerNotes,
  onEditCustomerNotes,
  bookingSource,
  onSourceUpdate,
  businessId,
  bookingId
}: BookingCustomerInfoProps) {
  const navigate = useNavigate();
  const hasCustomerInfoAccess = true; // No longer using permissions
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [tempAddress, setTempAddress] = useState('');
  const [addressKey, setAddressKey] = useState(0); // Force re-render of AddressAutocomplete

  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [tempNotes, setTempNotes] = useState(customerNotes || '');
  const [isSourceDropdownOpen, setIsSourceDropdownOpen] = useState(false);
  const sourceButtonRef = useRef<HTMLSpanElement>(null);
  
  // Customer Source state
  const [selectedCustomerSource, setSelectedCustomerSource] = useState<CustomerSource | null>(null);
  
  // Common source options
  const sourceOptions = [
    'Google',
    'Facebook',
    'Instagram', 
    'Referral',
    'Repeat Customer',
    'Walk-in',
    'Phone Call',
    'Email',
    'Website',
    'Other'
  ];

  // Update temp notes when customerNotes prop changes
  useEffect(() => {
    setTempNotes(customerNotes || '');
  }, [customerNotes]);

  // Close dropdown when clicking outside or scrolling
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.source-dropdown') && !target.closest('[data-source-dropdown]')) {
        setIsSourceDropdownOpen(false);
      }
    };

    const handleScroll = () => {
      setIsSourceDropdownOpen(false);
    };

    if (isSourceDropdownOpen) {
      document.addEventListener('click', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleScroll);
      return () => {
        document.removeEventListener('click', handleClickOutside);
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleScroll);
      };
    }
  }, [isSourceDropdownOpen]);





  // Set initial customer source based on booking's source
  useEffect(() => {
    if (bookingSource) {
      setSelectedCustomerSource({
        id: bookingSource,
        business_id: businessId || '',
        name: bookingSource,
        is_active: true,
        created_at: '',
        updated_at: ''
      });
    }
  }, [bookingSource, businessId]);



  // Reset and re-initialize AddressAutocomplete when editing starts
  const handleStartEditing = () => {
    setTempAddress(bookingAddress || '');
    setIsEditingAddress(true);
    // Only force re-render when starting to edit, not during selection
    setAddressKey(prev => prev + 1);
  };

  if (!hasCustomerInfoAccess) {
    return null;
  }

  return (
    <div className="relative">
      <div className="space-y-4">
        {/* Customer Info */}
        <div className="flex items-center space-x-3">
          {/* Clickable customer info container */}
          <div 
            className={`flex items-center space-x-3 flex-1 border border-gray-200 rounded-lg px-4 py-3 ${
              canEdit && onEdit ? 'cursor-pointer hover:bg-gray-50 hover:border-gray-300 transition-all' : ''
            }`}
            onClick={() => {
              if (canEdit && onEdit) {
                onEdit();
              }
            }}
          >
            <div className="h-12 w-12 rounded-full bg-blue-500 flex items-center justify-center text-white font-medium text-sm">
              {customer.first_name[0]}{customer.last_name[0]}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-base font-medium text-gray-900 truncate">
                {customer.first_name} {customer.last_name}
              </div>
              {/* Mobile: Stack vertically with phone first, Desktop: Horizontal */}
              <div className="flex flex-col space-y-1 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-4 text-sm text-gray-600">
                {customer.phone && (
                  <div className="flex items-center space-x-2">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <span className="truncate">{customer.phone}</span>
                  </div>
                )}
                {customer.email && (
                  <div className="flex items-center space-x-2">
                    <Mail className="h-4 w-4 text-gray-400" />
                    <span className="truncate">{customer.email}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Action buttons */}
          <div className="flex items-center space-x-2 flex-shrink-0">
            {/* Phone icon */}
            {customer.phone && (
              <button 
                className="p-3 -m-1 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-all active:scale-95"
                onClick={() => window.open(`tel:${customer.phone}`, '_self')}
                title={`Call ${customer.phone}`}
              >
                <Phone className="h-5 w-5" />
              </button>
            )}
            
            {/* Message icon */}
            <button 
              className="p-3 -m-1 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-all active:scale-95"
              onClick={() => {
                // Detect current page to pass as source
                const currentPath = window.location.pathname;
                let sourcePage = 'bookings'; // default
                if (currentPath === '/calendarV2') {
                  sourcePage = 'calendar';
                } else if (currentPath === '/crm') {
                  sourcePage = 'crm';
                }
                
                navigate(`/messages?customerId=${customer.id}${bookingId ? `&bookingId=${bookingId}` : ''}${sourcePage ? `&source=${sourcePage}` : ''}`);
              }}
              title="Open messages for this customer"
            >
              <MessageSquare className="h-5 w-5" />
            </button>
          </div>
        </div>
        
        {/* Source - Dropdown */}
        {(canEdit && onSourceUpdate) || (selectedCustomerSource || bookingSource) ? (
          <div className="text-xs text-gray-400 mt-1 relative source-dropdown">
            <span 
              ref={sourceButtonRef}
              onClick={() => {
                if (canEdit && onSourceUpdate) {
                  setIsSourceDropdownOpen(!isSourceDropdownOpen);
                }
              }}
              className={canEdit && onSourceUpdate ? 'cursor-pointer hover:text-gray-600 transition-colors' : ''}
            >
              Source: {selectedCustomerSource ? selectedCustomerSource.name : (bookingSource || 'Select source...')}
              {canEdit && onSourceUpdate && (
                <ChevronDown className="inline-block w-3 h-3 ml-0.5" />
              )}
            </span>
          </div>
        ) : null}
        {/* Booking Address - Mobile bookings only */}
        {mobile_booking && bookingAddress && (
          <div className="flex items-start space-x-2">
            <MapPin className="h-3 w-3 text-gray-400 mt-0.5" />
            <div 
              className={`flex-1 ${
                canEdit && onEditBookingAddress ? 'cursor-pointer hover:opacity-70 transition-opacity active:scale-[0.98] rounded-lg -m-1 p-1' : ''
              }`}
              onClick={() => {
                if (canEdit && onEditBookingAddress) {
                  handleStartEditing();
                }
              }}
            >
              <span className="text-xs text-gray-600">
                {bookingAddress}
              </span>
            </div>
          </div>
        )}

        {/* Customer Notes - Minimal */}
        {(customerNotes || (canEdit && onEditCustomerNotes)) && (
          <div className="text-xs text-gray-500">
            {isEditingNotes && onEditCustomerNotes ? (
              <textarea
                value={tempNotes}
                onChange={(e) => setTempNotes(e.target.value)}
                onBlur={() => {
                  if (tempNotes !== customerNotes) {
                    onEditCustomerNotes(tempNotes);
                  }
                  setIsEditingNotes(false);
                }}
                placeholder="Add customer notes..."
                className="w-full h-16 rounded bg-gray-50 border border-gray-200 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 text-xs px-2 py-1.5 resize-none"
                autoFocus
              />
            ) : (
              <div 
                onClick={() => canEdit && onEditCustomerNotes && setIsEditingNotes(true)}
                className={`${customerNotes ? 'text-gray-600' : 'text-gray-400 italic'} ${canEdit && onEditCustomerNotes ? 'cursor-text hover:text-gray-700 transition-colors' : ''}`}
              >
                {customerNotes || 'Click to add customer notes...'}
              </div>
            )}
          </div>
        )}
        
        {/* Address editing state */}
        {isEditingAddress && mobile_booking && onEditBookingAddress && (
          <div className="space-y-2">
            <AddressAutocomplete
              key={addressKey}
              value={tempAddress}
              onChange={(address) => setTempAddress(address)}
              onSelect={(address) => {
                onEditBookingAddress(address.formatted);
                setIsEditingAddress(false);
                setTempAddress('');
              }}
              placeholder="Enter booking address"
              className="w-full h-8 rounded border border-gray-200 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 text-sm px-2"
            />
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => {
                  setIsEditingAddress(false);
                  setTempAddress('');
                }}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>



      {/* Source Dropdown Portal */}
      {isSourceDropdownOpen && sourceButtonRef.current && createPortal(
        <div 
          data-source-dropdown
          className="fixed bg-white border border-gray-200 rounded-md shadow-lg z-[9999] min-w-[150px]"
          style={{
            left: sourceButtonRef.current.getBoundingClientRect().left,
            top: sourceButtonRef.current.getBoundingClientRect().bottom + 4,
          }}
        >
          <div 
            onClick={() => {
              onSourceUpdate?.(null);
              setIsSourceDropdownOpen(false);
            }}
            className="px-3 py-1.5 text-xs hover:bg-gray-50 cursor-pointer text-gray-500"
          >
            No source
          </div>
          {sourceOptions.map((source) => (
            <div
              key={source}
              onClick={() => {
                onSourceUpdate?.(source);
                setIsSourceDropdownOpen(false);
              }}
              className={`px-3 py-1.5 text-xs hover:bg-gray-50 cursor-pointer ${
                bookingSource === source ? 'bg-indigo-50 text-indigo-600' : 'text-gray-700'
              }`}
            >
              {source}
            </div>
          ))}
        </div>,
        document.body
      )}

    </div>
  );
}